// Saving a REVIEW answer on the device: the question's row under the version
// (made the first time it is answered, changed after). Saved here first and
// synced like every other record; when it reaches CRM, the API records who
// reviewed the version and the day (hig_reviewedby / hig_reviewedon).
import { answerFromFields, answerRowFields, type Answer, type ReviewAnswer, type ReviewQuestionFromApi } from '@higgins/m2q-pricing';
import { oneAtATime } from './condition-records';
import { getRecord, patchFields, saveRecord, type DeviceRecord } from './device-db';
import { syncNow } from './outbox';
import { reviewAnswerId } from '@higgins/condition-library';

export type AnswerChange = { answer: Answer | null } | { comment: string };

const applied = (entry: Pick<ReviewAnswer, 'answer' | 'comment'>, change: AnswerChange) => ({
  answer: 'answer' in change ? change.answer : entry.answer,
  comment: 'comment' in change ? change.comment || null : entry.comment,
});

/**
 * Answer a question (null clears it) or change its comment, on the row as
 * stored now. A question with no row yet starts from `shown` — what the old
 * JSON held for it, if anything — so its other half isn't lost. A cleared row
 * is kept, blank, so the old JSON never shows through again.
 */
export function saveReviewAnswer(version: DeviceRecord, question: ReviewQuestionFromApi, shown: ReviewAnswer | undefined, change: AnswerChange): Promise<void> {
  return oneAtATime(async () => {
    const id = reviewAnswerId(version.id, question.key);
    if (await getRecord(id)) {
      await patchFields(id, (stored) => answerRowFields(question, applied(answerFromFields(stored), change)));
    } else {
      await saveRecord({
        id,
        table: 'reviewanswer',
        jobId: version.jobId,
        parentId: version.id,
        etag: null,
        fields: answerRowFields(question, applied(shown ?? { answer: null, comment: null }, change)),
      });
    }
    void syncNow();
  });
}
